import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, Trash2, Copy, Check } from 'lucide-react'
import { format } from 'date-fns'
import { listExtensionTokens, mintExtensionToken, revokeExtensionToken } from '@/lib/api'
import type { ExtensionToken } from '@/schemas'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'

export function ExtensionTokens() {
  const qc = useQueryClient()
  const [mintOpen, setMintOpen] = useState(false)
  const [userAgent, setUserAgent] = useState('')
  const [mintedToken, setMintedToken] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['extension-tokens'],
    queryFn: listExtensionTokens,
  })

  const mintMutation = useMutation({
    mutationFn: () => mintExtensionToken(userAgent || undefined),
    onSuccess: (res) => {
      setMintedToken(res.token)
      qc.invalidateQueries({ queryKey: ['extension-tokens'] })
    },
    onError: () => toast.error('Failed to mint token.'),
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeExtensionToken(id),
    onSuccess: () => {
      toast.success('Token revoked.')
      qc.invalidateQueries({ queryKey: ['extension-tokens'] })
    },
    onError: () => toast.error('Could not revoke token.'),
  })

  async function copyToken() {
    if (!mintedToken) return
    await navigator.clipboard.writeText(mintedToken)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function closeMintDialog() {
    setMintOpen(false)
    setMintedToken(null)
    setUserAgent('')
    setCopied(false)
  }

  const tokens = data?.tokens ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Extension Tokens</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage authentication tokens for the Chrome extension.
          </p>
        </div>
        <Button size="sm" onClick={() => setMintOpen(true)}>
          <Plus className="h-4 w-4" />
          Mint new token
        </Button>
      </div>

      <div className="rounded-lg border bg-card">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !tokens.length ? (
          <div className="py-14 text-center text-sm text-muted-foreground">
            No tokens issued yet. Mint one to connect the extension.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Token ID</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                    User agent
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                    Last used
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Issued</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <TokenRow
                    key={token.token_id}
                    token={token}
                    onRevoke={() => revokeMutation.mutate(token.token_id)}
                    revoking={revokeMutation.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mint dialog */}
      <Dialog open={mintOpen} onOpenChange={closeMintDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mint new token</DialogTitle>
            <DialogDescription>
              The token is shown once. Copy it immediately — it cannot be retrieved later.
            </DialogDescription>
          </DialogHeader>

          {!mintedToken ? (
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label>User agent label (optional)</Label>
                <Input
                  placeholder="e.g. Work laptop Chrome"
                  value={userAgent}
                  onChange={(e) => setUserAgent(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Helps identify which device this token belongs to.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={closeMintDialog}>
                  Cancel
                </Button>
                <Button onClick={() => mintMutation.mutate()} disabled={mintMutation.isPending}>
                  {mintMutation.isPending ? 'Minting…' : 'Mint token'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 pt-2">
              <div className="rounded-md border bg-muted p-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Your new token</p>
                <p className="font-mono text-xs break-all select-all">{mintedToken}</p>
              </div>
              <Button onClick={copyToken} variant="outline" className="w-full">
                {copied ? (
                  <>
                    <Check className="h-4 w-4 text-green-600" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy to clipboard
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Paste this token into the Chrome extension options page.
              </p>
              <div className="flex justify-end">
                <Button onClick={closeMintDialog}>Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function TokenRow({
  token,
  onRevoke,
  revoking,
}: {
  token: ExtensionToken
  onRevoke: () => void
  revoking: boolean
}) {
  const isRevoked = Boolean(token.revoked_at)

  return (
    <tr className="border-b last:border-0 hover:bg-muted/30 transition-colors">
      <td className="px-3 py-2.5 font-mono text-xs">{token.token_id}</td>
      <td className="px-3 py-2.5 text-sm text-muted-foreground">
        {token.user_agent ?? '—'}
      </td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground">
        {token.last_used_at
          ? format(new Date(token.last_used_at), 'd MMM yyyy HH:mm')
          : 'Never'}
      </td>
      <td className="px-3 py-2.5">
        <Badge variant={isRevoked ? 'destructive' : 'default'} className="text-xs">
          {isRevoked ? 'Revoked' : 'Active'}
        </Badge>
      </td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground">
        {format(new Date(token.created_at), 'd MMM yyyy')}
      </td>
      <td className="px-3 py-2.5 text-right">
        {!isRevoked && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={onRevoke}
            disabled={revoking}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </td>
    </tr>
  )
}
